/**
 * HOP DONG CO DINH — docs/05-phan-quyen.md §5.2 va §5.3.
 *
 * 30 capability id chia lam hai tang doc lap (OQ-245 go 2 cai cua chuyen quyen so huu):
 *   - Tang HE THONG (16): dom.* | share.* | conn.* | adm.*      -> system_roles.capabilities
 *   - Tang TEN MIEN (16): dns.* | zone.* | g.*                  -> domain_roles.capabilities
 *
 * KHONG doi ten id. FE, seed data va test deu tham chieu (docs/00 §"Quy uoc").
 */

export interface CapabilityDef {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly group: string;
}

/* ─────────────────────────────────────────────────────────────
 * §5.2 Capability tang he thong (16) — roles.html
 * ───────────────────────────────────────────────────────────── */

export const SYSTEM_CAPABILITY_GROUPS = {
  domain: 'Tên miền',
  share: 'Chia sẻ',
  connection: 'Kết nối Cloudflare',
  admin: 'Quản trị',
} as const;

export const SYSTEM_CAPABILITIES = [
  {
    id: 'dom.view',
    name: 'Xem tên miền được gán',
    description: 'Chỉ tên miền do mình thêm hoặc được cấp',
    group: SYSTEM_CAPABILITY_GROUPS.domain,
  },
  {
    id: 'dom.viewall',
    name: 'Xem toàn bộ tên miền hệ thống',
    description: 'Bỏ qua BR-1 — cờ quyền lực nhất',
    group: SYSTEM_CAPABILITY_GROUPS.domain,
  },
  {
    id: 'dom.add',
    name: 'Thêm tên miền mới',
    description: 'Kết nối zone Cloudflare vào hệ thống',
    group: SYSTEM_CAPABILITY_GROUPS.domain,
  },
  {
    id: 'dom.delete',
    name: 'Xoá tên miền',
    description: 'Gỡ zone khỏi hệ thống',
    group: SYSTEM_CAPABILITY_GROUPS.domain,
  },
  {
    id: 'dom.plan',
    name: 'Đổi gói & cấu hình SSL/TLS',
    description: 'Chế độ mã hoá, HSTS, always-HTTPS',
    group: SYSTEM_CAPABILITY_GROUPS.domain,
  },

  {
    id: 'share.grant',
    name: 'Chia sẻ tên miền cho người khác',
    description: 'Cấp quyền trên tên miền mình sở hữu',
    group: SYSTEM_CAPABILITY_GROUPS.share,
  },
  {
    id: 'share.revoke',
    name: 'Thu hồi quyền của người khác',
    description: 'Gỡ tên miền khỏi danh sách của họ',
    group: SYSTEM_CAPABILITY_GROUPS.share,
  },

  {
    id: 'conn.own',
    name: 'Quản lý kết nối của mình',
    description: 'Thêm, sửa, xoá token Cloudflare cá nhân',
    group: SYSTEM_CAPABILITY_GROUPS.connection,
  },
  {
    id: 'conn.all',
    name: 'Quản lý kết nối của mọi người',
    description: 'Xem và sửa mọi kết nối trong hệ thống',
    group: SYSTEM_CAPABILITY_GROUPS.connection,
  },
  {
    id: 'conn.default',
    name: 'Đặt kết nối mặc định hệ thống',
    description: 'Kết nối dùng khi người thêm tên miền chưa có token riêng',
    group: SYSTEM_CAPABILITY_GROUPS.connection,
  },

  {
    id: 'adm.users',
    name: 'Quản lý người dùng',
    description: 'Mời, khoá, đặt lại mật khẩu, buộc bật 2FA',
    group: SYSTEM_CAPABILITY_GROUPS.admin,
  },
  {
    id: 'adm.roles',
    name: 'Quản lý loại quyền',
    description: 'Tạo, sửa, xoá vai trò hệ thống',
    group: SYSTEM_CAPABILITY_GROUPS.admin,
  },
  {
    id: 'adm.domainroles',
    name: 'Quản lý quyền tên miền',
    description: 'Định nghĩa mức quyền cấp trên từng tên miền',
    group: SYSTEM_CAPABILITY_GROUPS.admin,
  },
  {
    id: 'adm.settings',
    name: 'Cài đặt hệ thống',
    description: 'SMTP, chính sách bảo mật, thương hiệu',
    group: SYSTEM_CAPABILITY_GROUPS.admin,
  },
  {
    id: 'adm.audit',
    name: 'Xem nhật ký toàn hệ thống',
    description: 'Nhật ký của mọi người dùng',
    group: SYSTEM_CAPABILITY_GROUPS.admin,
  },
] as const satisfies readonly CapabilityDef[];

/* ─────────────────────────────────────────────────────────────
 * §5.3 Capability tang ten mien (16) — domain-roles.html
 * ───────────────────────────────────────────────────────────── */

export const DOMAIN_CAPABILITY_GROUPS = {
  dns: 'Bản ghi DNS',
  zone: 'Vùng DNS',
  sharing: 'Chia sẻ & sở hữu',
} as const;

export const DOMAIN_CAPABILITIES = [
  {
    id: 'dns.view',
    name: 'Xem bản ghi DNS',
    description: 'Mở tab DNS records của tên miền',
    group: DOMAIN_CAPABILITY_GROUPS.dns,
  },
  {
    id: 'dns.create',
    name: 'Thêm bản ghi',
    description: 'Tạo A, AAAA, CNAME, MX, TXT, SRV',
    group: DOMAIN_CAPABILITY_GROUPS.dns,
  },
  {
    id: 'dns.edit',
    name: 'Sửa bản ghi',
    description: 'Đổi giá trị, tên, ưu tiên của bản ghi có sẵn',
    group: DOMAIN_CAPABILITY_GROUPS.dns,
  },
  {
    id: 'dns.delete',
    name: 'Xoá bản ghi',
    description: 'Gỡ bản ghi khỏi vùng DNS',
    group: DOMAIN_CAPABILITY_GROUPS.dns,
  },
  {
    id: 'dns.proxy',
    name: 'Bật / tắt proxy Cloudflare',
    description: 'Chuyển giữa proxied và DNS only',
    group: DOMAIN_CAPABILITY_GROUPS.dns,
  },
  {
    id: 'dns.ttl',
    name: 'Đổi TTL',
    description: 'Thay thời gian sống của bản ghi',
    group: DOMAIN_CAPABILITY_GROUPS.dns,
  },
  {
    id: 'dns.io',
    name: 'Nhập / xuất zone file',
    description: 'Import BIND, export toàn bộ bản ghi',
    group: DOMAIN_CAPABILITY_GROUPS.dns,
  },

  {
    id: 'zone.view',
    name: 'Xem tổng quan tên miền',
    description: 'Trạng thái, nameserver, gói, lưu lượng',
    group: DOMAIN_CAPABILITY_GROUPS.zone,
  },
  {
    id: 'zone.ssl',
    name: 'Cấu hình SSL/TLS',
    description: 'Chế độ mã hoá, HSTS, always-HTTPS',
    group: DOMAIN_CAPABILITY_GROUPS.zone,
  },
  {
    id: 'zone.plan',
    name: 'Đổi gói Cloudflare',
    description: 'Nâng / hạ gói của vùng DNS',
    group: DOMAIN_CAPABILITY_GROUPS.zone,
  },
  {
    id: 'zone.cache',
    name: 'Xoá cache',
    description: 'Purge toàn bộ hoặc theo đường dẫn',
    group: DOMAIN_CAPABILITY_GROUPS.zone,
  },
  {
    id: 'zone.ns',
    name: 'Đổi nameserver & xác minh lại',
    description: 'Chạy lại quy trình kích hoạt vùng',
    group: DOMAIN_CAPABILITY_GROUPS.zone,
  },

  {
    id: 'g.share',
    name: 'Chia sẻ tên miền này',
    description: 'Cấp quyền cho người dùng khác',
    group: DOMAIN_CAPABILITY_GROUPS.sharing,
  },
  {
    id: 'g.revoke',
    name: 'Thu hồi quyền người khác',
    description: 'Gỡ tên miền khỏi danh sách của họ',
    group: DOMAIN_CAPABILITY_GROUPS.sharing,
  },
  {
    id: 'g.remove',
    name: 'Xoá tên miền khỏi hệ thống',
    description: 'Gỡ vùng DNS khỏi DNSHub',
    group: DOMAIN_CAPABILITY_GROUPS.sharing,
  },

  /*
   * ── Sub-domain (OQ-291) ─────────────────────────────────────────────────
   *
   * Hai quyền này thuộc tầng TÊN MIỀN, không phải tầng sub-domain — và đó
   * chính là cách quy tắc "một cấp" tồn tại được.
   *
   * Chủ dự án: *"Chỉ có thể tạo và chia sẻ sub-domain 1 cấp sau domain chính
   * chứ người được chia sẻ cũng không tạo và chia sẻ sub của sub-domain nữa."*
   *
   * Quyền hiệu lực trên một sub-domain lọc qua `SUB_ALLOWED_CAPABILITIES`, và
   * hai id dưới đây **không nằm trong danh sách ấy**. Nên dù ai đó gán mức
   * `owner` cho một sub-domain, người nhận vẫn không tạo được sub cấp hai: họ
   * không có quyền, chứ không phải bị giấu nút.
   */
  {
    id: 'sub.create',
    name: 'Tạo tên miền con',
    description: 'Tạo tên miền con một cấp dưới tên miền này',
    group: DOMAIN_CAPABILITY_GROUPS.sharing,
  },
  {
    id: 'sub.share',
    name: 'Chia sẻ tên miền con',
    description: 'Cấp quyền quản lý một tên miền con cho người dùng khác',
    group: DOMAIN_CAPABILITY_GROUPS.sharing,
  },
] as const satisfies readonly CapabilityDef[];

/**
 * Quyền được phép có hiệu lực ở cấp SUB-DOMAIN — danh sách CHO PHÉP (OQ-291).
 *
 * Cho phép chứ không phải cấm, vì hai danh sách hỏng theo hai hướng ngược nhau:
 * quên thêm vào danh sách cấm thì quyền mới **tự lọt xuống** cấp sub và không
 * ai biết; quên thêm vào danh sách cho phép thì nó **không có tác dụng** và
 * người dùng báo ngay.
 *
 * Sai theo hướng thứ hai gây phiền. Sai theo hướng thứ nhất là lỗ hổng.
 *
 * Loại ra có chủ đích:
 *   - `zone.*` — thuộc về cả vùng DNS: SSL, gói, cache, nameserver. Một người
 *     giữ `blog.example.com` không có lý do gì đổi được gói của `example.com`.
 *   - `g.*` — chia sẻ/thu hồi/xoá cả TÊN MIỀN, không phải sub.
 *   - `sub.create`, `sub.share` — chính là quy tắc một cấp.
 */
export const SUB_ALLOWED_CAPABILITIES = [
  'dns.view',
  'dns.create',
  'dns.edit',
  'dns.delete',
  'dns.proxy',
  'dns.ttl',
  'dns.io',
] as const satisfies readonly DomainCapability[];

const SUB_ALLOWED_SET = new Set<string>(SUB_ALLOWED_CAPABILITIES);

/** Lọc một tập quyền về đúng những gì có nghĩa ở cấp sub-domain. */
export function capabilitiesForSubdomain(caps: readonly string[]): string[] {
  return caps.filter((c) => SUB_ALLOWED_SET.has(c));
}

export type SystemCapability = (typeof SYSTEM_CAPABILITIES)[number]['id'];
export type DomainCapability = (typeof DOMAIN_CAPABILITIES)[number]['id'];
export type Capability = SystemCapability | DomainCapability;

export const SYSTEM_CAPABILITY_IDS: readonly SystemCapability[] = SYSTEM_CAPABILITIES.map(
  (c) => c.id,
);
export const DOMAIN_CAPABILITY_IDS: readonly DomainCapability[] = DOMAIN_CAPABILITIES.map(
  (c) => c.id,
);
export const ALL_CAPABILITY_IDS: readonly Capability[] = [
  ...SYSTEM_CAPABILITY_IDS,
  ...DOMAIN_CAPABILITY_IDS,
];

const SYSTEM_SET = new Set<string>(SYSTEM_CAPABILITY_IDS);
const DOMAIN_SET = new Set<string>(DOMAIN_CAPABILITY_IDS);

export function isSystemCapability(id: string): id is SystemCapability {
  return SYSTEM_SET.has(id);
}

export function isDomainCapability(id: string): id is DomainCapability {
  return DOMAIN_SET.has(id);
}

export function isKnownCapability(id: string): id is Capability {
  return SYSTEM_SET.has(id) || DOMAIN_SET.has(id);
}

/**
 * Cap nao thuoc tang he thong — docs/05 §5.1 `can()`.
 * Tien to dom. | share. | conn. | adm. => quyet dinh boi system_roles.capabilities.
 */
export const SYSTEM_CAPABILITY_PREFIXES = ['dom.', 'share.', 'conn.', 'adm.'] as const;

export function isSystemLayer(cap: string): boolean {
  return SYSTEM_CAPABILITY_PREFIXES.some((p) => cap.startsWith(p));
}

/** BR-2: co cap nay thi bo qua BR-1 va thay/lam duoc moi thu tren tang ten mien. */
export const CAP_VIEW_ALL_DOMAINS = 'dom.viewall' as const;

/** Nhom capability tra ve cho UI — GET /roles/capabilities, GET /domain-roles/capabilities. */
export function groupCapabilities(
  caps: readonly CapabilityDef[],
): { group: string; capabilities: CapabilityDef[] }[] {
  const out: { group: string; capabilities: CapabilityDef[] }[] = [];
  for (const cap of caps) {
    let bucket = out.find((g) => g.group === cap.group);
    if (!bucket) {
      bucket = { group: cap.group, capabilities: [] };
      out.push(bucket);
    }
    bucket.capabilities.push(cap);
  }
  return out;
}
